import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, Search } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BrokerFindDriversTab = () => {
  const [drivers, setDrivers] = useState(null);
  const [connectedIds, setConnectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", phone: "" });
  const [inviting, setInviting] = useState(false);

  const loadNetwork = () =>
    brokerApi.get("/network").then(({ data }) => setConnectedIds(data.map((c) => c.driver_id).filter(Boolean))).catch(() => {});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/drivers`).then(({ data }) => setDrivers(data.drivers)).catch(() => setDrivers([]));
    loadNetwork();
  }, []);

  const connect = async (driverId) => {
    try {
      await brokerApi.post("/network/connect", { driver_id: driverId });
      toast.success("Driver added to your network.");
      loadNetwork();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add this driver.");
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await brokerApi.post("/network/invite", inviteForm);
      toast.success(`Invited ${inviteForm.name} to your network.`);
      setInviteForm({ name: "", email: "", phone: "" });
      loadNetwork();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send this invite.");
    } finally {
      setInviting(false);
    }
  };

  const filtered = (drivers || []).filter((d) => d.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="broker-find-drivers-tab" className="space-y-10">
      <div>
        <h2 className="font-heading text-xl font-bold text-rr-navy mb-4">Browse the Driver Directory</h2>
        <div className="relative max-w-sm mb-4">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-rr-muted" />
          <input
            data-testid="find-drivers-search-input"
            placeholder="Search drivers by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-rr-line rounded-full pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-rr-orange"
          />
        </div>
        {drivers === null ? (
          <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-rr-orange" /></div>
        ) : filtered.length === 0 ? (
          <p data-testid="find-drivers-empty-state" className="text-rr-muted text-sm">No drivers found in the public directory yet.</p>
        ) : (
          <div data-testid="find-drivers-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((d) => {
              const connected = connectedIds.includes(d.id);
              return (
                <div key={d.id} data-testid={`find-drivers-card-${d.id}`} className="bg-white border border-rr-line rounded-xl p-4">
                  <p className="font-heading font-bold text-rr-navy">{d.name}</p>
                  <p className="text-xs text-rr-muted mt-0.5">
                    {[d.city, d.state].filter(Boolean).join(", ") || "Location on request"}{d.cdl_class && ` · CDL ${d.cdl_class}`}
                  </p>
                  <button
                    disabled={connected}
                    onClick={() => connect(d.id)}
                    data-testid={`find-drivers-connect-button-${d.id}`}
                    className={`w-full mt-3 text-xs font-bold py-2 rounded-full transition-colors ${
                      connected ? "bg-rr-green/10 text-rr-green cursor-default" : "bg-rr-navy text-white hover:bg-rr-orange"
                    }`}
                  >
                    {connected ? "In Your Network" : "Add to Network"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-heading text-xl font-bold text-rr-navy mb-4">Invite a Driver Directly</h2>
        <form onSubmit={submitInvite} data-testid="broker-invite-driver-form" className="bg-white border border-rr-line rounded-2xl p-6 space-y-3 max-w-md">
          <input
            required
            data-testid="invite-driver-name-input"
            placeholder="Driver name"
            value={inviteForm.name}
            onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
          />
          <input
            required
            type="email"
            data-testid="invite-driver-email-input"
            placeholder="Driver email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
          />
          <input
            data-testid="invite-driver-phone-input"
            placeholder="Driver phone (optional)"
            value={inviteForm.phone}
            onChange={(e) => setInviteForm((f) => ({ ...f, phone: e.target.value }))}
            className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
          />
          <button type="submit" disabled={inviting} data-testid="invite-driver-submit-button" className="bg-rr-orange text-white font-bold px-6 py-3 rounded-full hover:bg-rr-navy transition-colors flex items-center gap-2 disabled:opacity-60">
            {inviting ? <Loader2 size={16} className="animate-spin" /> : <><UserPlus size={15} /> Send Invite</>}
          </button>
        </form>
      </div>
    </div>
  );
};
